import { initialState } from './ai-session-context-plugin';
import type {
	AIContentPositionEntry,
	SessionContextAction,
	SessionContextPluginState,
} from './types';

/**
 * Apply a display-indexed update over nested stream-review segments.
 *
 * `streamReviewSegments` is stored as top-level entries with optional nested
 * `diffSegments`, while PSR actions (undo/redo/accept/position writeback) address
 * the flattened visible list index. This helper walks entries in that same visible
 * order and applies `transform` to the addressed leaf segment, preserving the
 * original nested shape in state.
 */
const mapDisplaySegments = ({
	segments,
	transform,
}: {
	segments: AIContentPositionEntry[];
	transform: (
		entry: AIContentPositionEntry,
		displayIndex: number,
	) => AIContentPositionEntry | undefined;
}): AIContentPositionEntry[] => {
	let displayIndex = 0;
	return segments.map((entry) => {
		if (entry.isAccepted) {
			return entry;
		}
		if (entry.diffSegments?.length) {
			let changed = false;
			const nextDiffSegments = entry.diffSegments.map((diffEntry) => {
				if (diffEntry.isAccepted) {
					return diffEntry;
				}
				const next = transform(diffEntry, displayIndex);
				displayIndex += 1;
				if (next !== undefined) {
					changed = true;
					return next;
				}
				return diffEntry;
			});
			return changed ? { ...entry, diffSegments: nextDiffSegments } : entry;
		}
		const next = transform(entry, displayIndex);
		displayIndex += 1;
		return next !== undefined ? next : entry;
	});
};

/**
 * Reduces AI experience context actions to update the plugin state.
 * Handles actions for resetting, prompt submission, streaming, completion, and errors.
 *
 * @param state - The current AI experience context plugin state
 * @param action - The action to process
 * @returns The updated AI experience context plugin state
 */
export function reducer({
	state,
	action,
}: {
	action: SessionContextAction;
	state: SessionContextPluginState;
}): SessionContextPluginState {
	switch (action.type) {
		case 'RESET':
			return initialState;
		case 'START_SESSION':
			return {
				...state,
				sessionId: action.sessionId,
				status: 'streaming',
				// Single source of truth read by buildCommitTransaction and endAISession
				// to suppress Post Stream Review for a create stream.
				isCreateSession: action.isCreateSession,
			};
		case 'PROMPT':
			return {
				...state,
				status: 'prompt',
			};
		case 'STREAM':
			// Prevent streaming if the session is in an error state
			if (state.status === 'error') {
				return {
					...state,
				};
			}
			// if the previous state was complete it means we are starting a new stream
			const responseVersion = state.status === 'complete' ? 0 : state.responseVersion + 1;
			return {
				...state,
				adfResponse: action.adfResponse,
				markdown: action.markdown,
				status: 'streaming',
				responseVersion,
			};
		case 'COMPLETE':
			// Prevent completing the session if it is in an error state
			if (state.status === 'error') {
				return {
					...state,
				};
			}
			return {
				...state,
				adfResponse: action.adfResponse,
				markdown: action.markdown,
				responseTooSimilar: action.responseTooSimilar,
				status: 'complete',
				// If this completion is for a queue-timeout force-finished tool call,
				// remember its id so the next `buildCommitTransaction` persists its
				// positions for resume.
				pendingForceAbortedToolCallId: action.forceAbortedToolCallId,
			};
		case 'ERROR':
			return {
				...state,
				errorInfo: action.errorInfo,
				status: 'error',
			};
		case 'UPDATE_TRACE_IDS':
			return {
				...state,
				traceIds: action.traceIds,
			};
		case 'UPDATE_ID_MAP':
			return {
				...state,
				idMap: { ...state.idMap, ...action.idMap },
			};
		case 'CANCEL_STREAM':
			// Set the cancelRequested flag so the plugin's view.update() can abort the network request.
			// The subsequent RESET action will clear this flag along with the rest of the state.
			return {
				...state,
				cancelRequested: true,
			};
		case 'PAUSE_STREAM':
			return {
				...state,
				streamPaused: true,
			};
		case 'RESUME_STREAM':
			return {
				...state,
				streamPaused: false,
			};
		case 'SET_HAS_USER_SCROLLED':
			return {
				...state,
				hasUserScrolled: true,
			};
		case 'START_FREE_GEN_STREAM':
			return {
				...state,
				status: 'streaming',
				userInput: action.userInput,
			};
		// [CCI-17658] Per-segment rework: start streaming but preserve
		// aiContentPositions and streamReviewSegments. Snapshot the target
		// segment's range for in-bounds detection after the commit.
		case 'START_SEGMENT_REWORK':
			return {
				...state,
				status: 'streaming',
				userInput: action.userInput,
				reworkSnapshot: {
					topLevelIndex: action.topLevelIndex,
					startPos: action.startPos,
					endPos: action.endPos,
				},
			};
		case 'REWORK_APPEND_SLICE': {
			const streamReviewSegments = state.streamReviewSegments ?? [];
			if (action.topLevelIndex < 0 || action.topLevelIndex >= streamReviewSegments.length) {
				return state;
			}
			// [CCI-17658] Append a new coarse entry rather than mapping by
			// topLevelIndex — aiContentPositions and streamReviewSegments are
			// not index-aligned (coarse entries accumulate across all tool calls).
			const newCoarseEntry: AIContentPositionEntry = {
				startPos: action.startPos,
				endPos: action.endPos,
				slices: [action.originalUserContentSlice, action.newSlice],
				appliedIndex: 1,
			};
			return {
				...state,
				aiContentPositions: [...(state.aiContentPositions ?? []), newCoarseEntry],
				streamReviewSegments: streamReviewSegments.map((entry, i) => {
					if (i !== action.topLevelIndex) {
						return entry;
					}
					return {
						...entry,
						slices: [...entry.slices, action.newSlice],
						appliedIndex: entry.slices.length,
						startPos: action.startPos,
						endPos: action.endPos,
						// Diff-level segments are stale after replacing the whole top-level
						// range with a newly reworked slice; they will be recomputed on reopen.
						diffSegments: undefined,
					};
				}),
			};
		}
		// [CCI-15904] Ends the AI session but retains aiContentPositions so the
		// Post Stream Review toolbar can navigate them. Resets ephemeral stream
		// state (sessionId, status, adfResponse) but NOT the positions array.
		//
		// (afm-ai-pr-review-bot #8) Also clear `currentStreamReviewSegmentIndex`
		// and `currentStreamReviewAiOptIn` to defend against a brand-new AI
		// session starting before the user closes the post-stream-review
		// toolbar (which would otherwise hit `RESET`). Without clearing,
		// `buildCommitTransaction` could append fresh entries into a new
		// `aiContentPositions` array while the stale index from the previous
		// session points out-of-bounds into the shorter new array.
		case 'END_SESSION': {
			// [CCI-17658] For per-segment rework, preserve streamReviewSegments and
			// mode so update() / endAISession can reopen PSR at the correct segment.
			// reworkSnapshot is the authoritative signal that an active per-segment
			// rework is in progress.
			const isPerSegmentRework = !!state.reworkSnapshot;
			return {
				...state,
				sessionId: undefined,
				status: 'idle',
				adfResponse: undefined,
				markdown: undefined,
				cancelRequested: false,
				currentStreamReviewSegmentIndex: isPerSegmentRework
					? state.currentStreamReviewSegmentIndex
					: undefined,
				currentStreamReviewAiOptIn: undefined,
				currentStreamReviewMode: isPerSegmentRework ? state.currentStreamReviewMode : undefined,
				streamReviewSegments: isPerSegmentRework ? state.streamReviewSegments : undefined,
				reworkSnapshot: undefined,
				pendingForceAbortedToolCallId: undefined,
				// Clear so a subsequent update stream is treated as a normal review session.
				isCreateSession: undefined,
			};
		}
		case 'POST_STREAM_REVIEW': {
			let next: SessionContextPluginState = {
				...state,
				status: 'postStreamReview',
				currentStreamReviewSegmentIndex: action.currentStreamReviewSegmentIndex,
				currentStreamReviewMode: action.mode ?? state.currentStreamReviewMode ?? 'aggregate',
				// Only overwrite the captured aiOptIn when the action provides one — keeps
				// the originally-captured value across subsequent UPDATE_STREAM_REVIEW_SEGMENT
				// re-opens (which are also dispatched as POST_STREAM_REVIEW from openAIModal).
				currentStreamReviewAiOptIn: action.aiOptIn ?? state.currentStreamReviewAiOptIn,
			};
			// Apply the optional Undo/Redo applied-index update in the SAME pass, so the
			// modal open and the state change are atomic (no follow-up dispatch). Each
			// `{ index, appliedIndex }` sets that fine `streamReviewSegments` entry's
			// applied slice; aggregate Undo/Redo passes many updates (only the segments
			// it re-applied — skipping accepted / already-in-state ones), step-through
			// passes one.
			if (action.appliedIndexUpdate) {
				const byIndex = new Map(
					action.appliedIndexUpdate.updates.map((u) => [u.index, u.appliedIndex]),
				);
				next = {
					...next,
					streamReviewSegments: mapDisplaySegments({
						segments: next.streamReviewSegments ?? [],
						transform: (entry, displayIndex) => {
							const nextIndex = byIndex.get(displayIndex);
							return nextIndex === undefined ? undefined : { ...entry, appliedIndex: nextIndex };
						},
					}),
				};
			}
			// Mark the accepted segment in the same pass as the re-open, so the
			// working set and the new anchor stay consistent.
			if (action.acceptedUpdate) {
				const { acceptedUpdate } = action;
				next = {
					...next,
					streamReviewSegments: mapDisplaySegments({
						segments: next.streamReviewSegments ?? [],
						transform: (entry, displayIndex) =>
							displayIndex === acceptedUpdate.index ? { ...entry, isAccepted: true } : undefined,
					}),
				};
			}
			// Overwrite the ranges of the segments aggregate Undo/Redo just
			// re-applied with authoritative positions. This runs AFTER the plugin's
			// generic remap (which cannot re-separate collapsed / coincident ranges),
			// so it is the last word on those entries' positions.
			if (action.positionsUpdate) {
				const byIndex = new Map(action.positionsUpdate.positions.map((p) => [p.index, p]));
				next = {
					...next,
					streamReviewSegments: mapDisplaySegments({
						segments: next.streamReviewSegments ?? [],
						transform: (entry, displayIndex) => {
							const pos = byIndex.get(displayIndex);
							return pos ? { ...entry, startPos: pos.startPos, endPos: pos.endPos } : undefined;
						},
					}),
				};
			}
			return next;
		}
		case 'UPDATE_STREAM_REVIEW_SEGMENT':
			return {
				...state,
				currentStreamReviewSegmentIndex: action.currentStreamReviewSegmentIndex,
			};
		case 'UPDATE_AI_CONTENT_POSITION': {
			const next = (state.aiContentPositions ?? []).map((entry, i) =>
				i === action.index ? { ...entry, startPos: action.startPos, endPos: action.endPos } : entry,
			);
			return {
				...state,
				aiContentPositions: next,
			};
		}
		// Remove a stored aborted tool call once it has been resumed.
		case 'CLEAR_ABORTED_TOOL_CALL_POSITION': {
			if (!state.abortedToolCallPositions?.[action.toolCallId]) {
				return state;
			}
			const { [action.toolCallId]: _removed, ...rest } = state.abortedToolCallPositions;
			return {
				...state,
				abortedToolCallPositions: rest,
			};
		}
		// Replace the fine step-through segment list (populate on
		// entering step-through, `[]` to clear on returning to aggregate).
		case 'SET_STREAM_REVIEW_SEGMENTS':
			return {
				...state,
				streamReviewSegments: action.segments,
			};
		default:
			return state;
	}
}
